import { engineFieldAttributes } from './engine-field-attributes.js';
import type { FieldPathDotSeparated } from '@adornis/baseql/entities/field-path-resolution.js';
import type { Form } from '@adornis/forms/form.js';
import { getAllCountries } from '@adornis/internationalization/helpers/flag.js';
import { Language } from '@adornis/translation-core/languages.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { format } from 'date-fns';
import { html } from 'lit';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import type { RegisterStudentData } from '../../api/registerStudentWithSuSForm.js';

export type RegistrationPath = Array<string | number>;
export interface RegistrationFieldOptions {
  label: string;
  type?: 'text' | 'email' | 'tel' | 'date' | 'textarea';
  options?: readonly (string | number | boolean | { value: string | number | boolean; label: string })[];
  /** Suggestions for a free text field, like the address picker. */
  search?: (query: string) => Promise<string[]>;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  inputmode?: 'text' | 'numeric' | 'email' | 'tel';
  onPicked?: (value: unknown) => void;
}

export const countryOptions = Object.entries(getAllCountries(Language.GERMAN))
  .map(([code, name]) => ({ value: code, label: name }))
  .sort((a, b) => a.label.localeCompare(b.label, 'de'));
export const yesNo = [
  { value: true, label: 'Ja' },
  { value: false, label: 'Nein' },
];

/** Keep the existing entity validation and draft persistence; render the functional Engine fields. */
export function RegistrationField(
  form: Form<RegisterStudentData>,
  path: RegistrationPath,
  options: RegistrationFieldOptions,
) {
  const name = path.join('.');
  const id = `sus-${name.replaceAll('.', '-')}`;
  return form.renderField<RegisterStudentData>(name as FieldPathDotSeparated<RegisterStudentData>, field => {
    const value: unknown = field.value;
    // pickValueForField (unlike field.onPicked) runs the form's onValuePicked, which persists the draft.
    const pick = (next: unknown) => {
      form.pickValueForField(path, next);
      options.onPicked?.(next);
    };
    const choices = options.options?.map(option =>
      typeof option === 'object' ? option : { value: option, label: String(option) },
    );
    const common = {
      ariaLabel: options.label,
      disabled: options.disabled || field.disabled,
      error: field.error,
      onPicked: pick,
    };
    const input = options.search
      ? DropdownSelectionInput<string>({
          ...common,
          value: typeof value === 'string' ? value : null,
          search: options.search,
          freeText: true,
          clearable: !options.required,
          placeholder: 'Suchen oder eingeben',
          emptyState: 'Keine passenden Einträge',
        })
      : choices
        ? DropdownSelectionInput({
            ...common,
            value: choices.find(choice => choice.value === value) ?? null,
            selectables: choices,
            resolveID: choice => String(choice.value),
            onPicked: choice => pick(choice?.value ?? null),
            renderString: choice => choice.label,
            searchable: choices.length > 10,
            clearable: !options.required,
            placeholder: 'Bitte auswählen',
            emptyState: 'Keine passenden Einträge',
          })
        : options.type === 'date'
          ? DateInput({
              ...common,
              // `type: 'date'` reads and writes canonical calendar days (00:00 UTC of the day meant). A
              // restored draft went through JSON, so the value may arrive as an ISO string.
              type: 'date',
              value: value ? toCalendarDay(value instanceof Date ? value : new Date(String(value).slice(0, 10))) : null,
              max: format(new Date(), 'yyyy-MM-dd'),
            })
          : options.type === 'textarea'
            ? TextareaInput({ ...common, value: String(value ?? ''), rows: 5 })
            : TextInput({
                ...common,
                value: String(value ?? ''),
                type: options.type === 'email' ? 'email' : 'text',
                autocomplete: options.autocomplete,
              });
    return html`<div
      class="flex min-w-0 flex-col gap-2 [&_input]:text-base [&_textarea]:text-base"
      ${engineFieldAttributes({ id, name, label: options.label, required: options.required, error: field.error, type: options.type === 'tel' ? 'tel' : undefined })}
    >
      <label class="text-sm font-semibold leading-relaxed text-base-content/70" for=${id}
        >${options.label}${options.required ? ' *' : ''}</label
      >
      ${input}
    </div>`;
  });
}
